import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type Query,
  type QueryConstraint,
} from "firebase/firestore";

import { db } from "../FirebaseConfig";

/**
 * Live-subscribed list of all docs in a collection. Returns the docs as
 * an array with `id` merged in. `null` while loading, `[]` when empty,
 * `Error` when the snapshot listener fails.
 */
export function useCollection<T>(
  collectionName: string,
  ...constraints: QueryConstraint[]
): { rows: (T & { id: string })[] | null; error: Error | null } {
  const [rows, setRows] = useState<(T & { id: string })[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Stable dep: stringify constraints so the effect re-subscribes only
  // when caller intent actually changes.
  const depKey = JSON.stringify(constraints.map((c) => c.toString()));

  useEffect(() => {
    let cancelled = false;
    let unsub: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    function subscribe() {
      let q: Query = collection(db, collectionName);
      for (const c of constraints) q = query(q, c);
      if (constraints.length === 0) q = query(q, orderBy("__name__"));
      unsub = onSnapshot(
        q,
        (snap) => {
          if (cancelled) return;
          const list: (T & { id: string })[] = [];
          snap.forEach((d) =>
            list.push({ id: d.id, ...(d.data() as T) })
          );
          setRows(list);
          setError(null);
          attempts = 0;
        },
        (err) => {
          if (cancelled) return;
          setError(err);
          // Snapshot errors are usually permission-denied during the
          // tiny window where the auth context hasn't finished
          // resolving the Usuarios doc, or transient SDK issues on
          // mobile. The SDK doesn't auto-retry permission failures, so
          // back off + re-subscribe a few times before giving up.
          attempts += 1;
          if (attempts <= 4) {
            const delayMs = Math.min(8000, 500 * 2 ** (attempts - 1));
            retryTimer = setTimeout(() => {
              if (cancelled) return;
              try {
                unsub?.();
              } catch {
                /* ignore */
              }
              subscribe();
            }, delayMs);
          }
        }
      );
    }

    subscribe();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        unsub?.();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, depKey]);

  return { rows, error };
}
